<script setup lang="ts">
import { useAsyncState, useLocalStorage } from '@vueuse/core'
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { Form } from 'vee-validate'
import type { GenericObject } from 'vee-validate'
import { object, string } from 'yup'
import { useToast } from 'vue-toastification'
import { useRoute, useRouter } from 'vue-router'
import { useLanguageService } from '@/composables/language'
import HeroLiveMessages from '@/components/HeroLiveMessages.vue'
import LandingFooter from '@/components/layout/LandingFooter.vue'
import LandingNavbar from '@/components/layout/LandingNavbar.vue'
import vanIcon from '@/assets/images/van.svg'
import lorryIcon from '@/assets/images/lorry.svg'
import bigLorryIcon from '@/assets/images/big_lorry.svg'
import lkwIcon from '@/assets/images/lkw.svg'
import locationIcon from '@/assets/images/location.svg'
import mailIcon from '@/assets/images/email.svg'
import mapSvg from '@/assets/images/map.svg'
import AddressAutocompleteInput from '@/components/form/AddressAutocompleteInput.vue'
import TextInput from '@/components/form/TextInput.vue'
import { TypeEnum, VehicleTypeEnum } from '@apiTypes'
import type { CountryEnum, QuoteCreateByID, Waypoint } from '@apiTypes'
import { useAddressValidator } from '@/composables/address-validation'
import { emailValidator } from '@/utils/validators'
import { collectAndShowMessages } from '@/utils/address-validation'
import { useQuote } from '@/composables/quotes'
import { useAuthStore } from '@/stores/auth'
import { getUserTimeZone } from '@/utils'
import type { Address } from '@/utils'
import { landingUrl } from '@/utils/landing'
import blogCover1 from '@/assets/images/blog-cover-1.webp'

const vehicleTypes = [
  { type: VehicleTypeEnum.VAN, icon: vanIcon, titleKey: 'vehicles.van.title', descKey: 'vehicles.van.desc' },
  { type: VehicleTypeEnum.SMALL_LORRY, icon: lorryIcon, titleKey: 'vehicles.small_lorry.title', descKey: 'vehicles.small_lorry.desc' },
  { type: VehicleTypeEnum.BIG_LORRY, icon: bigLorryIcon, titleKey: 'vehicles.big_lorry.title', descKey: 'vehicles.big_lorry.desc' },
  { type: VehicleTypeEnum.LKW, icon: lkwIcon, titleKey: 'vehicles.lkw.title', descKey: 'vehicles.lkw.desc' },
]

const languageService = useLanguageService()
const toast = useToast()
const selectedVehicleType = ref(VehicleTypeEnum.VAN)

const quote = ref<Partial<QuoteCreateByID>>({
  waypoints: [],
  special_requirements: {},
  user: { email: '' },
})

const topAnchor = ref<HTMLElement | null>(null)

const { createQuote, loading, error } = useQuote()
const authStore = useAuthStore()
const { validate, hasIssues } = useAddressValidator()
const router = useRouter()
const route = useRoute()
const { t } = useI18n()
const isLoggedIn = computed(() => !!authStore.currentUser)

// The magic link of a canceled quote lands here (quoteByHashGuard): say why (INQ-820).
if (route.query.canceled === 'true') {
  toast.error(t('The quote has been canceled, please create a new one.'))

  const query = { ...route.query }

  delete query.canceled
  router.replace({ query })
}
const CACHE_DURATION = 24 * 60 * 60 * 1000
const RETRY_COUNT = 3
const RETRY_DELAY = 3000
interface BlogPost {
  title: string
  link: string
  description: string
  image: string
  pubDate: string
}

interface BlogCacheEntry {
  data: BlogPost[] | null
  timestamp: number | null
}

const blogCache = useLocalStorage<Record<string, BlogCacheEntry>>('blog-posts-cache', {})

const rssUrl = computed(() => {
  const lang = languageService.currentLanguage.value === 'en' ? '' : languageService.currentLanguage.value
  const langPrefix = lang ? `/${lang}` : ''

  return `https://blog.instacarrier.eu${langPrefix}/feed/atom/`
})

const blogUrl = landingUrl('/blog')

const reviews = [
  { id: 'patrick', rating: 5 },
  { id: 'dai', rating: 5 },
  { id: 'matthieu', rating: 5 },
  { id: 'kim', rating: 5 },
  { id: 'michael', rating: 5 },
  { id: 'elena', rating: 5 },
]

const reviewGroups = computed(() => {
  const result = []
  const chunkSize = 3

  for (let i = 0; i < reviews.length; i += chunkSize)
    result.push(reviews.slice(i, i + chunkSize))

  return result
})

const totalReviewSlides = computed(() => reviewGroups.value.length)
const currentReviewSlide = ref(0)
const reviewIntervalId = ref<number | null>(null)

const validationSchema = computed(() => {
  const baseSchema = {
    pickup: string().required('Pickup place is required'),
    delivery: string().required('Delivery place is required'),
  }

  if (!isLoggedIn.value) {
    return object({
      ...baseSchema,
      user: object({
        email: emailValidator.required('Email is required'),
      }),
    })
  }

  return object(baseSchema)
})

onMounted(() => {
  languageService.initializeLanguage()
})

function selectVehicle(type: VehicleTypeEnum) {
  selectedVehicleType.value = type
}

function scrollToTop() {
  if (typeof window === 'undefined')
    return

  const target = topAnchor.value
  const scrollContainer = (document.scrollingElement || document.documentElement || document.body) as HTMLElement

  if (target) {
    try {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
    catch {
      target.scrollIntoView()
    }
  }

  window.requestAnimationFrame(() => {
    const scrollOptions: ScrollToOptions = { top: 0, behavior: 'smooth' }

    try {
      scrollContainer.scrollTo(scrollOptions)
    }
    catch {
      scrollContainer.scrollTop = 0
      window.scrollTo(0, 0)
    }
  })
}

function updateAddress(type: TypeEnum.Pickup | TypeEnum.Delivery, address: Address, sequence: number) {
  if (!address.city || !address.country) {
    toast.warning(`Please specify ${type} which has at least city and country.`)

    return
  }

  // Google returns ISO 3166-1 alpha-2 country codes, which CountryEnum lists in full.
  const waypoint: Partial<Waypoint> = {
    city: address.city,
    country: address.country as CountryEnum,
    postcode: address.postal_code,
    street: address.street,
    street_number: address.home,
    name: address.name,
    point: address.point,
    sequence,
    type,
  }

  if (!quote.value.waypoints)
    quote.value.waypoints = []

  // A draft waypoint: id, created_at and updated_at are read-only fields the backend fills in.
  const idx = quote.value.waypoints.findIndex(wp => wp.type === type)
  if (idx > -1)
    quote.value.waypoints[idx] = waypoint as Waypoint
  else
    quote.value.waypoints.push(waypoint as Waypoint)
}

async function onSubmit(values: GenericObject) {
  const result = await validate(selectedVehicleType, quote)
  if (hasIssues.value) {
    collectAndShowMessages(result.value)

    return
  }

  quote.value = {
    ...quote.value,
    vehicle_type: selectedVehicleType.value,
  }

  if (authStore.currentUser) {
    quote.value.user = {
      ...authStore.currentUser,
      timezone: authStore.currentUser?.timezone || getUserTimeZone(),
    }
  }
  else {
    quote.value.user = {
      email: values.user.email,
      timezone: getUserTimeZone(),
    }
  }

  await createQuote(quote.value)
  if (!error.value) {
    toast.clear()
    await router.push('/free-quote')
  }
}

watch(error, value => value && toast.error(value))

function goToNextReview() {
  if (!totalReviewSlides.value)
    return

  currentReviewSlide.value = (currentReviewSlide.value + 1) % totalReviewSlides.value
}

function stopReviewRotation() {
  if (reviewIntervalId.value) {
    window.clearInterval(reviewIntervalId.value)
    reviewIntervalId.value = null
  }
}

function startReviewRotation() {
  if (typeof window === 'undefined' || !totalReviewSlides.value)
    return

  stopReviewRotation()
  reviewIntervalId.value = window.setInterval(goToNextReview, 4000)
}

watch(totalReviewSlides, count => {
  currentReviewSlide.value = 0
  if (count > 1)
    startReviewRotation()
  else
    stopReviewRotation()
})

function getCachedData(language: string) {
  return blogCache.value[language] || { data: null, timestamp: null }
}

function setCachedData(language: string, data: BlogCacheEntry) {
  blogCache.value[language] = data
}

function isCacheValid(language: string) {
  const cached = getCachedData(language)
  if (!cached.data || !cached.timestamp)
    return false
  if (Array.isArray(cached.data) && cached.data.length === 0)
    return false

  const cacheAge = Date.now() - cached.timestamp

  return cacheAge < CACHE_DURATION
}

function extractTextContent(htmlString: string) {
  const tempDiv = document.createElement('div')

  tempDiv.innerHTML = htmlString

  const textContent = tempDiv.textContent || tempDiv.innerText || ''
  const sentences = textContent.split(/[.!?]+/).filter(s => s.trim().length > 0)

  return sentences.slice(0, 2).join('. ') + (sentences.length > 2 ? '.' : '')
}

function extractImageFromContent(htmlContent: string) {
  if (!htmlContent)
    return null

  let cleanContent = htmlContent
  const cdataMatch = htmlContent.match(/<!\[CDATA\[(.*?)\]\]>/s)
  if (cdataMatch)
    cleanContent = cdataMatch[1]

  const tempDiv = document.createElement('div')

  tempDiv.innerHTML = cleanContent

  const imgElement = tempDiv.querySelector('img[src]')

  return imgElement ? imgElement.getAttribute('src') : null
}

function parseAtomFeed(xmlText: string) {
  const parser = new DOMParser()
  const xmlDoc = parser.parseFromString(xmlText, 'text/xml')

  const parserError = xmlDoc.querySelector('parsererror')
  if (parserError)
    throw new Error('Failed to parse RSS feed')

  const entries = xmlDoc.querySelectorAll('entry')
  const posts: BlogPost[] = []

  for (let i = 0; i < Math.min(3, entries.length); i++) {
    const entry = entries[i]
    const title = entry.querySelector('title')?.textContent || ''
    const link = entry.querySelector('link[rel="alternate"]')?.getAttribute('href') || ''
    const summary = entry.querySelector('summary')?.innerHTML || ''

    const published = entry.querySelector('published')?.textContent
                     || entry.querySelector('updated')?.textContent || ''

    const extractedImage = extractImageFromContent(summary)
    const summaryText = entry.querySelector('summary')?.textContent || ''
    const cleanDescription = extractTextContent(summaryText)

    const formattedDate = published
      ? new Date(published).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        })
      : ''

    posts.push({
      title: title.trim(),
      link: link.trim(),
      description: cleanDescription,
      image: extractedImage || blogCover1,
      pubDate: formattedDate,
    })
  }

  return posts
}

async function fetchWithRetry(url: string, retries = RETRY_COUNT) {
  for (let attempt = 1; attempt <= retries; attempt++) {
    try {
      const response = await fetch(url)
      if (!response.ok)
        throw new Error(`HTTP error! status: ${response.status}`)

      return await response.text()
    }
    catch (err) {
      if (attempt === retries)
        throw new Error(`Failed to fetch after ${retries} attempts: ${(err as Error).message}`)

      if (attempt < retries)
        await new Promise(resolve => setTimeout(resolve, RETRY_DELAY))
    }
  }

  throw new Error('Failed to fetch')
}

async function fetchBlogPostsFromAPI(url: string) {
  const proxyUrl = 'https://api.allorigins.win/raw?url='
  const fullUrl = proxyUrl + encodeURIComponent(url)
  const xmlText = await fetchWithRetry(fullUrl)

  return parseAtomFeed(xmlText)
}

const {
  state: blogPosts,
  isLoading: blogLoading,
  error: blogError,
  execute: loadBlogPosts,
} = useAsyncState(
  async () => {
    const currentLang = languageService.currentLanguage.value
    const currentRssUrl = rssUrl.value

    try {
      if (isCacheValid(currentLang)) {
        const cached = getCachedData(currentLang)

        return cached.data || []
      }

      const posts = await fetchBlogPostsFromAPI(currentRssUrl)

      if (posts.length > 0 && languageService.currentLanguage.value === currentLang) {
        setCachedData(currentLang, {
          data: posts,
          timestamp: Date.now(),
        })
      }

      return posts
    }
    catch (err) {
      const cached = getCachedData(currentLang)
      if (cached.data && cached.data.length > 0)
        return cached.data

      throw err
    }
  },
  [],
  {
    immediate: false,
    resetOnExecute: false,
  },
)

watch(
  () => languageService.currentLanguage.value,
  (newLang, oldLang) => {
    if (newLang !== oldLang)
      loadBlogPosts()
  },
)

onMounted(() => {
  startReviewRotation()
  loadBlogPosts()
})

onBeforeUnmount(() => {
  stopReviewRotation()
})
</script>

<template>
  <div class="hero-video">
    <video
      class="hero-video__media"
      autoplay
      muted
      loop
      preload="none"
      playsinline
    >
      <source src="@images/intersection.mp4" type="video/mp4">
    </video>
    <div class="hero-video__content">
      <div id="top-anchor" ref="topAnchor" class="top-anchor-marker" />
      <div class="hero-nav">
        <LandingNavbar overlay />
      </div>

      <!--  hero -->
      <section class="w-full hero-section">
        <div class="relative z-10 w-full max-w-[76rem] mx-auto px-0 sm:px-4 lg:px-12 pt-[4.375rem] pb-[5rem]">
          <div class="flex flex-col h-full space-y-10 py-10 px-4 sm:px-0 rounded-none sm:rounded-[40px] backdrop-blur-sm bg-black/35 road-hero-shadow">
            <div class="text-center lg:text-left text-[3rem] leading-[1] uppercase font-['Inter'] text-white font-normal lg:max-w-[35rem] mx-auto">
              {{ $t('hero.heading.prefix') }}
              <span class="text-[#d91a5d] font-black">{{ $t('hero.heading.highlight') }}</span>
              {{ $t('hero.heading.suffix') }}
            </div>

            <div class="lg:max-w-[40rem] mx-auto lg:mx-0 w-full space-y-8 lg:space-y-10">
              <div class="flex flex-col lg:flex-row space-y-2.5 lg:space-y-0 lg:space-x-2.5">
                <div
                  v-for="(vehicle, index) in vehicleTypes"
                  :key="`vehicle-${index}`"
                  class="w-full rounded-[16px] flex flex-col items-center justify-center space-y-4 py-6 lg:p-2 text-center lg:h-[133px] cursor-pointer transition-all duration-150"
                  :class="selectedVehicleType === vehicle.type ? 'bg-[#fdeef4] outline outline-2 outline-[#d81b5d] shadow-[0_0_16px_0_rgba(216,27,93,0.30)]' : 'bg-white shadow-[0px_4px_16px_0px_rgba(0,0,0,0.04)] hover:outline hover:outline-1 hover:outline-[rgba(216,27,93,0.45)]'"
                  @click="selectVehicle(vehicle.type)"
                >
                  <img :src="vehicle.icon" :alt="$t(vehicle.titleKey)" class="h-5 w-auto">
                  <div class="flex flex-col gap-1">
                    <span class="text-[14px] font-bold text-[#363636] leading-[1.5] text-center">{{ $t(vehicle.titleKey) }}</span>
                    <span class="text-[11px] font-medium text-[#363636] leading-[1.5] text-center">{{ $t(vehicle.descKey) }}</span>
                  </div>
                </div>
              </div>

              <div>
                <Form
                  class="hero-form flex flex-col space-y-4 lg:space-y-8"
                  :validation-schema="validationSchema"
                  @submit="onSubmit"
                >
                  <div class="flex flex-col lg:flex-row gap-3">
                    <div class="flex-1 flex items-center gap-3 rounded-[102px] bg-white shadow-[0px_4px_16px_0px_rgba(0,0,0,0.04)] px-4 py-3">
                      <img :src="locationIcon" alt="" class="w-[14.9px] h-[18.2px]">
                      <AddressAutocompleteInput
                        name="pickup"
                        label=""
                        required
                        border-danger
                        unstyled
                        :placeholder="$t('From')"
                        class="w-full"
                        css-class="hero-input w-full border-0 outline-none bg-transparent font-['Inter'] font-light text-[14px] leading-[1.5] text-[#626873]"
                        @address-change="updateAddress(TypeEnum.Pickup, $event, 1)"
                      />
                    </div>
                    <div class="flex-1 flex items-center gap-3 rounded-[102px] bg-white shadow-[0px_4px_16px_0px_rgba(0,0,0,0.04)] px-4 py-3">
                      <img :src="locationIcon" alt="" class="w-[14.9px] h-[18.2px]">
                      <AddressAutocompleteInput
                        name="delivery"
                        label=""
                        required
                        border-danger
                        unstyled
                        :placeholder="$t('To')"
                        class="w-full"
                        css-class="hero-input w-full border-0 outline-none bg-transparent font-['Inter'] font-light text-[14px] leading-[1.5] text-[#626873]"
                        @address-change="updateAddress(TypeEnum.Delivery, $event, 2)"
                      />
                    </div>
                  </div>

                  <div v-if="!isLoggedIn" class="flex items-center gap-3 rounded-[102px] bg-white shadow-[0px_4px_16px_0px_rgba(0,0,0,0.04)] px-4 py-3">
                    <img :src="mailIcon" alt="" class="w-[14.9px] h-[18.2px]">
                    <TextInput
                      name="user.email"
                      type="email"
                      autocomplete="email"
                      :placeholder="$t('E-mail')"
                      class="w-full"
                      css-class="hero-input w-full border-0 outline-none bg-transparent font-['Inter'] font-light text-[14px] leading-[1.5] text-[#626873]"
                      required
                      border-danger
                      unstyled
                      label=""
                    />
                  </div>

                  <div class="flex justify-center">
                    <button
                      type="submit"
                      class="hero-cta w-full sm:w-[262px] h-[57px] rounded-[82px] px-8 bg-[#d81b5d] text-white font-['Inter'] font-semibold text-[16px] leading-[1.5] tracking-[0] border-0"
                      :disabled="loading"
                    >
                      <span v-if="loading">{{ $t('Loading...') }}</span>
                      <span v-else>{{ $t('Get your price') }}</span>
                    </button>
                  </div>
                </Form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  </div>

  <div class="hero-messages-wrapper">
    <div class="w-[90%] lg:max-w-[76rem] mx-auto px-4 lg:px-12">
      <HeroLiveMessages />
    </div>
  </div>

  <!--  quick quote -->
  <section id="how-it-works" class="how-section w-[90%] lg:max-w-[76rem] mx-auto px-4 lg:px-12 py-12 lg:py-20 space-y-16">
    <div class="flex flex-col lg:flex-row gap-10 lg:gap-16 items-start">
      <div class="flex-1">
        <h6 class="text-[#d81b5d] text-sm font-semibold mb-3">
          {{ $t('HOW IT WORKS') }}
        </h6>
        <h2 class="font-['Inter'] font-bold text-[38px] leading-[125%] uppercase text-[#18191F]">
          {{ $t('Quick quote and book') }}
        </h2>
        <p class="text-[#4b5563] text-base lg:text-lg mt-4 mb-6 leading-relaxed">
          {{ $t('Get your transportation sorted quickly and easily. Request a quote, review the details, and book your shipment in just a few simple steps.') }}
        </p>
        <ul class="space-y-3 text-[#1f2937] font-semibold">
          <li class="flex items-center gap-3">
            <img src="@images/ic_checkmark.svg" alt="" class="w-4 h-4"> <span>{{ $t('Send a transportation request') }}</span>
          </li>
          <li class="flex items-center gap-3">
            <img src="@images/ic_checkmark.svg" alt="" class="w-4 h-4"> <span>{{ $t('Check your quote') }}</span>
          </li>
          <li class="flex items-center gap-3">
            <img src="@images/ic_checkmark.svg" alt="" class="w-4 h-4"> <span>{{ $t('Book your transport') }}</span>
          </li>
          <li class="flex items-center gap-3">
            <img src="@images/ic_checkmark.svg" alt="" class="w-4 h-4"> <span>{{ $t('Transport confirmed') }}</span>
          </li>
        </ul>
        <button
          type="button"
          class="how-cta inline-flex items-center justify-center mt-6 px-5 py-3 rounded-full bg-[#d81b5d] text-white font-[Inter] font-semibold text-[16px] leading-[150%] tracking-[0] border-0"
          @click="scrollToTop"
        >
          {{ $t('Get your price') }}
        </button>
      </div>
      <div class="flex-1 relative flex justify-end items-start">
        <img src="@images/man_on_pc.png" alt="man on pc" class="rounded-3xl shadow-lg how-man-pc">
        <img :src="mapSvg" alt="" class="how-dot-map">
      </div>
    </div>

    <div class="flex flex-col lg:flex-row-reverse gap-10 lg:gap-16 items-start">
      <div class="flex-1">
        <h6 class="text-[#d81b5d] text-sm font-semibold mb-3">
          {{ $t('HOW IT WORKS') }}
        </h6>
        <h2 class="font-['Inter'] font-bold text-[38px] leading-[125%] uppercase text-[#18191F]">
          {{ $t('Enjoy delivery') }}<br>
          {{ $t('and pay later') }}
        </h2>
        <p class="text-[#4b5563] text-base lg:text-lg mt-4 mb-6 leading-relaxed">
          {{ $t('Track your shipment, confirm delivery, and pay later. We make the process simple and transparent.') }}
        </p>
        <ul class="space-y-3 text-[#1f2937] font-semibold">
          <li class="flex items-center gap-3">
            <img src="@images/ic_checkmark.svg" alt="" class="w-4 h-4"> <span>{{ $t('Truck\'s licence plate info') }}</span>
          </li>
          <li class="flex items-center gap-3">
            <img src="@images/ic_checkmark.svg" alt="" class="w-4 h-4"> <span>{{ $t('Pickup confirmed') }}</span>
          </li>
          <li class="flex items-center gap-3">
            <img src="@images/ic_checkmark.svg" alt="" class="w-4 h-4"> <span>{{ $t('Delivery tracking') }}</span>
          </li>
          <li class="flex items-center gap-3">
            <img src="@images/ic_checkmark.svg" alt="" class="w-4 h-4"> <span>{{ $t('Delivery confirmed') }}</span>
          </li>
          <li class="flex items-center gap-3">
            <img src="@images/ic_checkmark.svg" alt="" class="w-4 h-4"> <span>{{ $t('Payment') }}</span>
          </li>
        </ul>
        <button
          type="button"
          class="how-cta inline-flex items-center justify-center mt-6 px-5 py-3 rounded-full bg-[#d81b5d] text-white font-[Inter] font-semibold text-[16px] leading-[150%] tracking-[0] border-0"
          @click="scrollToTop"
        >
          {{ $t('Get your price') }}
        </button>
      </div>
      <div class="flex-1 flex justify-start">
        <img src="@images/man_on_phone.png" alt="man on phone" class="rounded-3xl shadow-lg max-h-[420px] w-auto">
      </div>
    </div>
  </section>

  <!--  live transports -->
  <section class="moving-section py-12 lg:py-16">
    <video
      class="moving-video"
      autoplay
      muted
      loop
      preload="none"
      playsinline
    >
      <source src="@images/bridge.mp4" type="video/mp4">
    </video>
    <div class="moving-overlay" />
    <div class="w-[90%] lg:max-w-[76rem] mx-auto px-4 lg:px-12 relative z-10">
      <div class="flex justify-center">
        <div class="text-center text-white mt-[2rem]">
          <h1 class="moving-number">
            156
          </h1>
          <p class="moving-desc">
            {{ $t('Transports on the move') }}
          </p>
        </div>
      </div>
    </div>
  </section>

  <!-- certificates -->
  <section class="cert-section w-[90%] lg:max-w-[76rem] mx-auto px-4 lg:px-12 py-12 lg:py-16">
    <div class="flex justify-center mb-10">
      <h3 class="font-['Inter'] font-bold text-[38px] leading-[125%] text-center uppercase text-[#18191F]">
        {{ $t('We have received certificates') }}
      </h3>
    </div>
    <div class="flex flex-wrap justify-center items-center gap-6 md:gap-8">
      <a
        href="/static/files/iso_14001.pdf" target="_blank"
        rel="noopener"
        class="cert-item"
      >
        <img src="@images/iso_14001_color.webp" alt="ISO 14001" class="cert-img">
      </a>
      <a
        href="/static/files/iso_9001.pdf" target="_blank"
        rel="noopener"
        class="cert-item"
      >
        <img src="@images/iso_9001_color.webp" alt="ISO 9001" class="cert-img">
      </a>
      <a
        href="/static/files/aaa.pdf" target="_blank"
        rel="noopener"
        class="cert-item"
      >
        <img src="@images/aaa_color.webp" alt="AAA" class="cert-img cert-img-aaa">
      </a>
      <a
        href="/static/files/tisax.pdf" target="_blank"
        rel="noopener"
        class="cert-item"
      >
        <img src="@images/tisax_color.webp" alt="TISAX" class="cert-img">
      </a>
    </div>
  </section>

  <!-- reviews -->
  <section class="bg-[#210E34] py-12 lg:py-16">
    <div class="w-[90%] lg:max-w-[76rem] mx-auto px-4 lg:px-12">
      <div class="text-center text-white space-y-3">
        <h3 class="font-['Inter'] font-bold text-[38px] leading-[125%] uppercase text-white">
          {{ $t('Reviews') }}
        </h3>
        <p class="font-['Inter'] font-normal text-[16px] leading-[150%] text-center text-white">
          {{ $t('Your satisfaction is our priority.') }}
        </p>
      </div>

      <div class="mt-10 flex justify-center">
        <div
          v-for="(group, index) in reviewGroups"
          :key="`review-group-${index}`"
          :class="index === currentReviewSlide ? 'flex flex-wrap justify-center gap-[57px]' : 'hidden'"
        >
          <div
            v-for="review in group"
            :key="review.id"
            class="flex h-[298px] w-full max-w-[335px] flex-col rounded-[16px] bg-white p-6 shadow-[0px_4px_16px_0px_rgba(0,0,0,0.12)]"
          >
            <span class="quotes-icon" aria-hidden="true" />
            <p class="mt-4 mb-6 text-[16px] leading-[160%] text-[#D81B5D]">
              {{ $t(`reviews.${review.id}.text`) }}
            </p>
            <div class="mt-auto">
              <div class="flex items-center justify-between">
                <h5 class="m-0 text-[17px] font-semibold text-[#D81B5D]" :style="{ color: '#D81B5D' }">
                  {{ $t(`reviews.${review.id}.name`) }}
                </h5>
                <div class="flex text-[20px] leading-none text-[#ffc107]">
                  <span
                    v-for="n in 5"
                    :key="n"
                    :class="n <= review.rating ? '' : 'text-[#ddd]'"
                  >
                    ★
                  </span>
                </div>
              </div>
              <p class="mt-1 text-sm text-[#D81B5D]" :style="{ color: '#D81B5D' }">
                {{ $t(`reviews.${review.id}.title`) }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- blog -->
  <section class="w-[90%] lg:max-w-[76rem] mx-auto px-4 lg:px-12 py-12 lg:py-16">
    <div class="text-center mb-10">
      <h3 class="font-['Inter'] font-extrabold text-[34px] leading-[125%] text-[#18191F]">
        {{ $t('Latest news, guides and posts') }}
      </h3>
    </div>

    <div v-if="blogLoading" class="flex justify-center py-8">
      <div class="h-12 w-12 animate-spin rounded-full border-4 border-[#d81b5d]/30 border-t-[#d81b5d]" aria-label="Loading" />
    </div>

    <div
      v-else-if="blogError && (!blogPosts || blogPosts.length === 0)"
      class="flex flex-col items-center gap-4 text-center"
    >
      <p class="text-[#4b5563]">
        {{ $t('Unable to load blog posts') }}
      </p>
      <button
        type="button"
        class="inline-flex items-center justify-center rounded-[12px] border border-[#d81b5d] px-4 py-2 text-[#d81b5d] font-semibold"
        :disabled="blogLoading"
        @click="loadBlogPosts(0)"
      >
        {{ $t('Try Again') }}
      </button>
    </div>

    <div v-else-if="blogPosts && blogPosts.length > 0" class="flex flex-wrap justify-center gap-[57px]">
      <a
        v-for="post in blogPosts"
        :key="post.link"
        :href="post.link"
        target="_blank"
        rel="noopener noreferrer"
        class="group block h-full w-full max-w-[335px] no-underline-important"
      >
        <div class="h-full overflow-hidden rounded-[16px] bg-white transition duration-300 group-hover:-translate-y-1 no-underline-important">
          <img
            :src="post.image || blogCover1"
            :alt="post.title"
            class="h-[180px] w-full object-cover rounded-[16px]"
          >
          <div>
            <small class="mb-2 block text-left font-['Inter'] font-semibold text-[12px] leading-[150%] text-[#A1A1A1] no-underline-important pt-3 pb-1">{{ post.pubDate }}</small>
            <div class="flex h-full flex-col px-3">
              <h5 class="text-left font-['Inter'] font-extrabold text-[16px] leading-[125%] text-black no-underline-important">
                {{ post.title }}
              </h5>
              <p class="mt-2 flex-grow text-left font-['Inter'] font-normal text-[14px] leading-[150%] text-black no-underline-important">
                {{ post.description }}
              </p>
            </div>
          </div>
        </div>
      </a>
    </div>

    <div class="flex justify-center mt-10">
      <a
        class="inline-flex items-center justify-center gap-4 h-[50px] rounded-[8px] bg-[#D81B5D] px-8 py-[13px] text-white font-['Inter'] font-semibold text-[16px] leading-[150%] shadow-none no-underline-important"
        :href="blogUrl"
        target="_blank"
        rel="noopener noreferrer"
      >
        <img
          class="h-[11px] w-auto"
          src="@images/ic_arrow_outward.svg"
          alt="arrow icon"
        >
        {{ $t('Go to InstaCarrier Blog') }}
      </a>
    </div>
  </section>

  <!--  footer -->
  <LandingFooter />
</template>

<style scoped>
.hero-video {
  position: relative;
  overflow: hidden;
  background: #f1f2f6;
}

.hero-video__media {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 0;
}

.hero-video__content {
  position: relative;
  z-index: 1;
}

.hero-nav {
  min-height: 80px;
}

.hero-cta {
  border-radius: 82px !important;
}

.hero-messages-wrapper {
  position: relative;
  transform: translateY(-45%);
  margin-bottom: -2.5rem;
  z-index: 20;
}

@media (max-width: 768px) {
  .hero-messages-wrapper {
    transform: translateY(-40%);
    margin-bottom: -2rem;
  }
}

.moving-section {
  position: relative;
  overflow: hidden;
  min-height: 260px;
}

.moving-video {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 0;
  filter: grayscale(1);
}

.moving-overlay {
  position: absolute;
  inset: 0;
  background: #0f172a;
  opacity: 0.75;
  z-index: 1;
}

.moving-number {
  font-family: 'Inter', sans-serif;
  font-weight: 800;
  font-size: 46px;
  line-height: 125%;
  letter-spacing: 0%;
  color: #ffffff;
}

.moving-desc {
  margin-top: 0.5rem;
  font-family: 'Inter', sans-serif;
  font-weight: 700;
  font-size: 16px;
  line-height: 150%;
  letter-spacing: 0%;
  color: #ffffff;
}

.cert-section .cert-item {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  transition: all 0.3s ease;
  border-radius: 16px;
  min-height: 165px;
  min-width: 140px;
}

.cert-section .cert-img {
  max-height: 120px;
  max-width: 180px;
  width: auto;
  height: auto;
  transition: all 0.3s ease;
  filter: grayscale(1);
}

.cert-section .cert-item:hover .cert-img {
  transform: translateY(-4px) scale(1.05);
  filter: grayscale(0);
}

@media (max-width: 768px) {
  .cert-section .cert-item {
    width: 100%;
    justify-content: center;
  }

  .cert-section .cert-img {
    max-height: 110px;
    max-width: 160px;
  }
}

.how-dot-map {
  position: absolute;
  top: 10%;
  left: -18%;
  width: 80%;
  height: auto;
  opacity: 0.7;
  pointer-events: none;
  z-index: 1;
}

.how-man-pc {
  position: relative;
  z-index: 2;
  max-height: 420px;
  width: auto;
}

.how-cta {
  text-decoration: none !important;
  border: none;
  border-radius: 9999px;
}

.how-cta:hover,
.how-cta:focus,
.how-cta:active {
  text-decoration: none !important;
}

.quotes-icon {
  display: inline-block;
  width: 45px;
  height: 45px;
  background-color: #D81B5D;
  -webkit-mask: url("@images/quotes.webp") center/contain no-repeat;
  mask: url("@images/quotes.webp") center/contain no-repeat;
}

.no-underline-important {
  text-decoration: none !important;
}

.top-anchor-marker {
  position: absolute;
  top: 0;
  left: 0;
  width: 1px;
  height: 1px;
  pointer-events: none;
}

:global(html) {
  scroll-behavior: smooth;
}

.road-hero-shadow {
  box-shadow: rgba(0, 0, 0, 0.05) 0px 16px 48px 0px
}
</style>

<route lang="yaml">
meta:
  layout: tw
</route>
